import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  TCombatLogEntityType,
  TCombatLogSegmentListResponse,
  TCombatLogSegmentSummary
} from '../../../../shared/types/combatLogDownloadTypes';
import { apiServiceAsync } from '../../services/apiService';

const DOWNLOAD_ALL_DELAY_MS = 300;

export type TCombatLogSegmentTableRow = {
  userId: number;
  username: string | null;
  segment: TCombatLogSegmentSummary;
};

function triggerDownload(downloadUrl: string, filename?: string) {
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  if (filename) {
    link.download = filename;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function useCombatLogSegments(params: {
  isOpen: boolean;
  type: TCombatLogEntityType;
  correlationId: string;
}) {
  const [data, setData] = useState<TCombatLogSegmentListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [downloadingKeys, setDownloadingKeys] = useState<Record<string, boolean>>({});
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const latestRequestRef = useRef(0);

  const showLoadingState = params.isOpen && !loadError && (!data || isLoading);
  const sources = useMemo(() => (Array.isArray(data?.sources) ? data.sources : []), [data]);
  const rows = useMemo<TCombatLogSegmentTableRow[]>(
    () =>
      _.flatMap(sources, (source) =>
        source.segments.map((segment) => ({
          userId: source.userId,
          username: source.username,
          segment
        }))
      ),
    [sources]
  );
  const normalSegments = useMemo(() => rows.filter((row) => !row.segment.isError), [rows]);

  async function loadSegments(requestId = latestRequestRef.current) {
    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await apiServiceAsync.getCombatLogSegments({
        type: params.type,
        correlationId: params.correlationId
      });

      if (!response || !Array.isArray(response.sources)) {
        throw new Error('Invalid combat log segment response');
      }

      if (requestId !== latestRequestRef.current) {
        return;
      }

      setData(response as TCombatLogSegmentListResponse);
    } catch (err) {
      if (requestId !== latestRequestRef.current) {
        return;
      }

      setData(null);
      setLoadError('Failed to load segments. Please try again.');
    } finally {
      if (requestId === latestRequestRef.current) {
        setIsLoading(false);
      }
    }
  }

  useEffect(() => {
    if (!params.isOpen) {
      latestRequestRef.current += 1;
      return;
    }

    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    void loadSegments(requestId);

    return () => {
      if (latestRequestRef.current === requestId) {
        latestRequestRef.current += 1;
      }
    };
  }, [params.isOpen, params.type, params.correlationId]);

  async function downloadSegment(segmentKey: string, filename?: string) {
    setDownloadingKeys((current) => ({ ...current, [segmentKey]: true }));

    try {
      const response = await apiServiceAsync.postCombatLogDownload({ segmentKey, filename });
      triggerDownload(response.downloadUrl, response.filename);
    } catch (error) {
      console.error('Failed to download combat log segment', { error, segmentKey, filename });
    } finally {
      setDownloadingKeys((current) => {
        const next = { ...current };
        delete next[segmentKey];
        return next;
      });
    }
  }

  async function downloadAllSegments() {
    if (!normalSegments.length) {
      return;
    }

    setIsDownloadingAll(true);

    try {
      for (const row of normalSegments) {
        try {
          await downloadSegment(row.segment.segmentKey, row.segment.suggestedFilename);
        } catch (error) {
          console.error('Unexpected combat log download failure', {
            error,
            segmentKey: row.segment.segmentKey
          });
        }
        await new Promise((resolve) => setTimeout(resolve, DOWNLOAD_ALL_DELAY_MS));
      }
    } finally {
      setIsDownloadingAll(false);
    }
  }

  return {
    data,
    sources,
    rows,
    normalSegments,
    isLoading,
    loadError,
    showLoadingState,
    downloadingKeys,
    isDownloadingAll,
    loadSegments,
    downloadSegment,
    downloadAllSegments
  };
}
